import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';

// Глобальная очередь для загрузки файлов
const fileQueue = {
  queue: [],
  processing: false,
  currentFileId: null,
  processedIds: new Set(), // Храним ID уже обработанных файлов
  
  add: function(fileId, task) {
    // Пропускаем файлы без ID
    if (!fileId) {
      console.error('Попытка добавить файл без ID в очередь');
      return;
    }
    
    // Преобразуем ID в строку для унификации
    fileId = String(fileId);
    
    // Проверяем, обрабатывали ли мы уже этот файл
    if (this.processedIds.has(fileId)) {
      console.log(`Файл ${fileId} уже был обработан ранее, пропускаем`);
      return;
    }
    
    // Проверяем, обрабатывается ли файл сейчас
    if (this.currentFileId === fileId) {
      console.log(`Файл ${fileId} уже обрабатывается сейчас, пропускаем`);
      return;
    }
    
    // Проверяем, нет ли уже этого файла в очереди
    if (!this.queue.some(item => item.fileId === fileId)) {
      console.log(`Добавление файла ${fileId} в очередь загрузки`);
      this.queue.push({ fileId, task });
    } else {
      console.log(`Файл ${fileId} уже в очереди`);
      return;
    }
    
    // Запускаем обработку, если она еще не идет
    if (!this.processing) {
      this.processNext();
    }
  },
  
  processNext: function() {
    if (this.queue.length === 0) {
      console.log("Очередь загрузки пуста");
      this.processing = false;
      this.currentFileId = null;
      // Уведомляем об окончании загрузки
      this.notifyListeners(null);
      return;
    }
    
    this.processing = true;
    const { fileId, task } = this.queue.shift();
    this.currentFileId = fileId;
    
    console.log(`Обработка файла ${fileId} из очереди`);
    
    // Уведомляем слушателей о новом активном fileId
    this.notifyListeners(fileId);
    
    // Запускаем задачу и после её выполнения обрабатываем следующую
    task()
      .then(() => {
        console.log(`Файл ${fileId} успешно загружен`);
        // Добавляем файл в список обработанных
        this.processedIds.add(fileId);
        // Небольшая задержка перед следующим файлом
        setTimeout(() => this.processNext(), 1000);
      })
      .catch(error => {
        console.error(`Ошибка при загрузке файла ${fileId}:`, error);
        // НЕ добавляем файл с ошибкой в список обработанных
        // чтобы можно было повторить попытку
        console.log(`Файл ${fileId} не добавлен в обработанные из-за ошибки`);
        // Продолжаем с следующим файлом даже при ошибке
        setTimeout(() => this.processNext(), 1000);
      });
  },
  
  // Очищаем историю обработанных файлов только если это действительно необходимо
  resetProcessedHistory: function() {
    console.log('Полный сброс истории (используется только при retry)');
    this.processedIds.clear();
  },
  
  // Проверяем, нужно ли сбрасывать историю для данного набора файлов
  shouldResetHistory: function(fileIds) {
    const fileIdsSet = new Set(fileIds.map(id => String(id)));
    // Сбрасываем историю только если есть файлы, которые не были обработаны
    const hasUnprocessedFiles = Array.from(fileIdsSet).some(id => !this.processedIds.has(id));
    return hasUnprocessedFiles;
  },
  
  // Система подписки для отслеживания текущего файла
  listeners: [],
  
  subscribe: function(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  },
  
  notifyListeners: function(fileId) {
    this.listeners.forEach(callback => callback(fileId));
  }
};

// Глобальный кэш для хранения загруженных файлов
const fileCache = {
  cache: {},
  maxSize: 50, // Максимальное количество файлов в кэше
  
  get: function(fileId) {
    return this.cache[fileId];
  },
  
  set: function(fileId, data) {
    // Проверяем размер кэша и удаляем старые записи при необходимости
    const cacheEntries = Object.entries(this.cache);
    if (cacheEntries.length >= this.maxSize) {
      // Сортируем по времени и удаляем самые старые
      cacheEntries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const entriesToRemove = cacheEntries.slice(0, Math.floor(this.maxSize / 5));
      
      entriesToRemove.forEach(([key, value]) => {
        // Освобождаем URL перед удалением
        if (value.url) {
          URL.revokeObjectURL(value.url);
        }
        delete this.cache[key];
      });
    }
    
    // Добавляем новую запись
    this.cache[fileId] = data;
  },
  
  has: function(fileId) {
    return !!this.cache[fileId];
  },
  
  clear: function(fileId) {
    if (this.has(fileId)) {
      const cachedUrl = this.cache[fileId].url;
      if (cachedUrl) {
        URL.revokeObjectURL(cachedUrl);
      }
      delete this.cache[fileId];
    }
  }
};

const FilePreview = ({ file, dealId, index, totalFiles, onError }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [imageUrl, setImageUrl] = useState(null);
  const [fileType, setFileType] = useState('неизвестен');
  const [isCurrentlyLoading, setIsCurrentlyLoading] = useState(false);
  
  // Используем useRef для отслеживания активности компонента
  const isMounted = useRef(true);
  // Маркер запроса для контроля race condition
  const requestIdRef = useRef(0);

  const fileId = file?.id;

  // Отслеживаем, является ли этот файл текущим загружаемым файлом в очереди
  useEffect(() => {
    // Подписываемся на уведомления об изменении текущего загружаемого файла
    const unsubscribe = fileQueue.subscribe((currentFileId) => {
      if (isMounted.current) {
        setIsCurrentlyLoading(currentFileId === fileId);
      }
    });
    
    // Проверяем текущее состояние
    setIsCurrentlyLoading(fileQueue.currentFileId === fileId);
    
    return () => {
      unsubscribe();
    };
  }, [fileId]);

  if (!file || typeof file !== 'object') {
    console.error('Invalid file object:', file);
    return (
      <div className="p-4 bg-red-50 rounded-lg">
        <p className="text-red-500 text-sm">Ошибка: некорректные данные файла</p>
      </div>
    );
  }
  
  const token = localStorage.getItem('token');

  // Определяем тип файла на основе расширения URL или имени файла
  const detectFileType = useCallback((url, contentType) => {
    console.log(`Определение типа файла для ${fileId}:`, { url, contentType, fileName: file.name });
    
    // Сначала проверяем по content-type заголовку
    if (contentType) {
      if (contentType.includes('pdf')) {
        console.log(`Тип файла ${fileId}: PDF (по content-type)`);
        return 'pdf';
      }
      if (contentType.includes('image')) {
        console.log(`Тип файла ${fileId}: image (по content-type)`);
        return 'image';
      }
    }
    
    // Затем проверяем по URL или имени файла
    if (url) {
      if (url.toLowerCase().includes('.pdf') || url.toLowerCase().endsWith('.pdf')) {
        console.log(`Тип файла ${fileId}: PDF (по URL)`);
        return 'pdf';
      }
      if (/\.(jpg|jpeg|png|gif|bmp|webp|tiff|svg)$/i.test(url)) {
        console.log(`Тип файла ${fileId}: image (по URL)`);
        return 'image';
      }
    }
    
    // Проверяем имя файла, если есть
    if (file.name) {
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.pdf')) {
        console.log(`Тип файла ${fileId}: PDF (по имени файла)`);
        return 'pdf';
      }
      if (/\.(jpg|jpeg|png|gif|bmp|webp|tiff|svg)$/i.test(fileName)) {
        console.log(`Тип файла ${fileId}: image (по имени файла)`);
        return 'image';
      }
    }
    
    // Более осторожный подход - если не можем определить тип, попробуем как изображение
    console.log(`Не удалось определить тип файла ${fileId}, используем image по умолчанию`);
    return 'image';
  }, [file.name, fileId]);

  const loadImage = useCallback(async () => {
    // Если компонент размонтирован, ничего не делаем
    if (!isMounted.current) return null;
    
    // Увеличиваем идентификатор запроса
    const currentRequestId = ++requestIdRef.current;
    
    setLoading(true);
    setLoadingProgress(10);
    
    if (!fileId) {
      setError('Отсутствует ID файла');
      setLoading(false);
      if (onError) onError('Отсутствует ID файла');
      return null;
    }
    
    // Проверяем кэш
    if (fileCache.has(fileId)) {
      console.log(`Using cached file: ${fileId}`);
      const cachedData = fileCache.get(fileId);
      
      // Проверяем валидность кэшированных данных
      if (!cachedData.url || !cachedData.type) {
        console.warn(`Кэшированные данные для файла ${fileId} повреждены, удаляем из кэша`);
        fileCache.clear(fileId);
        // Продолжаем с загрузкой файла
      } else {
        // Проверяем, актуален ли компонент перед обновлением состояния
        if (isMounted.current && currentRequestId === requestIdRef.current) {
          setLoadingProgress(100);
          setFileType(cachedData.type);
          setImageUrl(cachedData.url);
          setLoading(false);
        }
        return Promise.resolve();
      }
    }
    
    console.log(`ОТЛАДКА ЗАГРУЗКИ ФАЙЛА ${fileId}:`);
    console.log(`- dealId: ${dealId}`);
    console.log(`- index: ${index}/${totalFiles}`);
    console.log(`- file object:`, file);
    console.log(`- file.url:`, file.url);
    console.log(`- file.name:`, file.name);
    console.log(`- file.id:`, file.id);
    
    // Проверяем, есть ли у файла прямая ссылка (B24 файлы)
    if (file.url && file.url.startsWith('http')) {
      console.log(`✅ Используем прямую ссылку для файла ${fileId}: ${file.url}`);

      // Определяем тип только по URL/имени файла (БЕЗ HEAD запроса к серверу)
      const detectedType = detectFileType(file.url, null);

      console.log(`Тип файла ${fileId} определен как: ${detectedType}`);

      // Сохраняем в кэш с прямой ссылкой
      fileCache.set(fileId, {
        type: detectedType,
        url: file.url,  // ПРЯМАЯ ССЫЛКА на Bitrix
        timestamp: Date.now()
      });

      // Сразу устанавливаем состояние (без HEAD запроса)
      if (isMounted.current && currentRequestId === requestIdRef.current) {
        setFileType(detectedType);
        setImageUrl(file.url);  // ПРЯМАЯ ССЫЛКА
        setLoadingProgress(100);
        setLoading(false);
      }

      return Promise.resolve();
    } else {
      console.log(`⚠️ Нет прямой ссылки, пытаемся загрузить через API. file.url:`, file.url);
    }
    
    try {
      setLoadingProgress(20);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // Таймаут 30 секунд
      
      // ИСПРАВЛЕНИЕ: Строгий поиск только в указанной сделке
      let url;
      let response;
      
      if (dealId) {
        // Используем строгий поиск только в конкретной сделке
        url = `${API_URL}/api/deal/${dealId}/file/${fileId}?token=${token}`;
        console.log(`Пытаемся загрузить через API: ${url}`);
        console.log(`Строгий поиск файла ${fileId} в сделке: ${dealId}`);
        
        response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
      } else {
        // Если dealId нет, возвращаем ошибку - мы больше не поддерживаем универсальный поиск
        setError('ID сделки обязателен для загрузки файла');
        setLoading(false);
        if (onError) onError('ID сделки обязателен для загрузки файла');
        return null;
      }
      
      // Проверяем, не был ли сделан новый запрос или размонтирован компонент
      if (!isMounted.current || currentRequestId !== requestIdRef.current) return null;
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Неизвестная ошибка');
        let errorMsg;
        
        console.error(`❌ Ошибка API для файла ${fileId}:`);
        console.error(`- Status: ${response.status}`);
        console.error(`- URL: ${url}`);
        console.error(`- Response text: ${errorText}`);
        
        if (response.status === 404) {
          errorMsg = `Файл ${fileId} не найден в сделке ${dealId}`;
        } else if (response.status === 500) {
          try {
            const errorData = JSON.parse(errorText);
            errorMsg = `Ошибка сервера: ${errorData.error || 'Неизвестная ошибка'}`;
            if (errorData.details) {
              console.error(`Детали ошибки для файла ${fileId}:`, errorData.details);
            }
          } catch {
            errorMsg = `Ошибка сервера: ${response.status}`;
          }
        } else {
          errorMsg = `Ошибка сервера: ${response.status} - ${errorText}`;
        }
        
        setError(errorMsg);
        setLoading(false);
        if (onError) onError(errorMsg);
        return null;
      } else {
        console.log(`✅ Успешный ответ API для файла ${fileId}, status: ${response.status}`);
      }
      
      // Логируем успешный результат
      console.log(`Файл ${fileId} успешно найден в сделке ${dealId}`);
      
      const contentType = response.headers.get('content-type');
      const detectedType = detectFileType(file.url, contentType);
      
      setLoadingProgress(50);
      
      // Получаем данные через ReadableStream для отслеживания прогресса
      const reader = response.body.getReader();
      const contentLength = +response.headers.get('Content-Length') || 0;
      let receivedLength = 0;
      const chunks = [];
      
      while(true) {
        const {done, value} = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        receivedLength += value.length;
        
        // Обновляем прогресс
        if (contentLength > 0) {
          const progress = Math.min(90, 50 + Math.round((receivedLength / contentLength) * 40));
          if (isMounted.current && currentRequestId === requestIdRef.current) {
            setLoadingProgress(progress);
          }
        }
        
        // Проверяем, не был ли сделан новый запрос или размонтирован компонент
        if (!isMounted.current || currentRequestId !== requestIdRef.current) {
          return null;
        }
      }
      
      // Собираем blob из чанков
      const blob = new Blob(chunks, { type: contentType });
      
      // Проверяем снова после асинхронных операций
      if (!isMounted.current || currentRequestId !== requestIdRef.current) {
        // Очистим созданный blob, если компонент уже не актуален
        return null;
      }
      
      setLoadingProgress(95);
      
      const blobUrl = URL.createObjectURL(blob);
      
      // Сохраняем в кэш
      fileCache.set(fileId, { 
        type: detectedType, 
        url: blobUrl,
        timestamp: Date.now()
      });
      
      if (isMounted.current && currentRequestId === requestIdRef.current) {
        setFileType(detectedType);
        setImageUrl(blobUrl);
        setLoadingProgress(100);
        setLoading(false);
      }
      
      return Promise.resolve();
      
    } catch (err) {
      console.error(`Error loading file ${fileId}:`, err);
      
      // Обновляем состояние только если компонент все еще актуален
      if (isMounted.current && currentRequestId === requestIdRef.current) {
        const errorMessage = err.name === 'AbortError' 
          ? 'Превышено время ожидания ответа' 
          : `Ошибка загрузки файла: ${err.message}`;
          
        setError(errorMessage);
        setLoading(false);
        if (onError) onError(errorMessage);
      }
      
      return Promise.reject(err);
    }
  }, [fileId, dealId, token, detectFileType, file.url, file.name, index, totalFiles, onError]);

  // Обработка монтирования/размонтирования
  // Определяем, является ли устройство мобильным
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           window.innerWidth <= 768;
  };

  useEffect(() => {
    isMounted.current = true;
    
    // Проверяем кэш сразу при монтировании
    if (fileCache.has(fileId)) {
      const cachedData = fileCache.get(fileId);
      setFileType(cachedData.type);
      setImageUrl(cachedData.url);
      setLoading(false);
      setLoadingProgress(100);
      console.log(`Файл ${fileId} загружен из кэша`);
    } else {
      const isMobile = isMobileDevice();
      console.log(`Устройство: ${isMobile ? 'мобильное' : 'десктоп'} - файл ${fileId}`);
      
      // На мобильных устройствах автоматически загружаем файлы
      if (isMobile) {
        console.log(`Мобильное устройство - автозагрузка файла ${fileId}`);
        setLoading(true);
        loadImage();
      } else {
        // На десктопе тоже автоматически загружаем, но через очередь
        console.log(`Десктоп - автозагрузка через очередь файла ${fileId}`);
        setLoading(false);
        
        const isInQueue = fileQueue.queue.some(item => item.fileId === fileId);
        const isCurrentlyProcessing = fileQueue.currentFileId === fileId;
        
        if (!isInQueue && !isCurrentlyProcessing) {
          fileQueue.add(fileId, () => {
            if (isMounted.current) {
              return loadImage();
            }
            return Promise.resolve();
          });
        }
      }
    }
    
    // Очистка при размонтировании
    return () => {
      isMounted.current = false;
    };
  }, [fileId, loadImage]);

  // Обработчик повторной загрузки
  const handleRetry = useCallback(() => {
    console.log(`Повторная попытка для файла ${fileId}`);
    setError(null);
    setLoading(true);
    setLoadingProgress(0);
    
    // Удаляем запись из кэша
    fileCache.clear(fileId);
    
    // Удаляем только этот файл из истории обработки
    fileQueue.processedIds.delete(String(fileId));
    
    // Немедленно выполняем загрузку без очереди
    loadImage().catch(error => {
      console.error(`Ошибка при retry для файла ${fileId}:`, error);
    });
  }, [fileId, loadImage]);

  // Рендеринг индикатора загрузки
  const renderLoadingIndicator = () => {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 bg-opacity-80 z-10">
        {/* Большой анимированный спиннер в центре */}
        <div className="mb-4">
          <div className="animate-spin w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
        
        <div className="w-64 bg-gray-200 rounded-full h-4 mb-2">
          <div 
            className="bg-blue-600 h-4 rounded-full transition-all duration-300" 
            style={{ width: `${loadingProgress}%` }}
          />
        </div>
        <div className="text-gray-600 text-sm font-medium">
          Загрузка ({loadingProgress}%)
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Файл {index + 1} из {totalFiles}
        </div>
        {dealId && (
          <div className="text-xs text-blue-600 mt-1">
            Сделка: {dealId}
          </div>
        )}
        {!dealId && (
          <div className="text-xs text-red-600 mt-1">
            ⚠️ Требуется ID сделки
          </div>
        )}
      </div>
    );
  };

  // Рендер содержимого на основе типа файла
  const renderContent = () => {
    if (!imageUrl || error) return null;
    
    console.log(`Рендер содержимого для файла ${fileId}: тип=${fileType}, URL=${imageUrl}`);
    
    if (fileType === 'pdf') {
      return (
        <div className="relative min-h-[600px] bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Заголовок с кнопками */}
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-2xl">📄</div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  {file.name || `Файл ${fileId}.pdf`}
                </h3>
                <p className="text-xs text-gray-500">PDF документ • Прямая ссылка</p>
              </div>
            </div>

            <div className="flex gap-2">
              {/* Кнопка "Открыть в новой вкладке" */}
              <button
                onClick={() => {
                  console.log(`Открытие PDF ${fileId} в новой вкладке`);
                  window.open(imageUrl, '_blank', 'noopener,noreferrer');
                }}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                title="Открыть в новой вкладке"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Открыть
              </button>

              {/* Кнопка "Скачать" */}
              <a
                href={imageUrl}
                download={file.name || `документ_${fileId}.pdf`}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                onClick={() => console.log(`Скачивание PDF ${fileId}`)}
                title="Скачать PDF"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Скачать
              </a>
            </div>
          </div>

          {/* PDF через iframe */}
          <iframe
            src={imageUrl}
            className="w-full h-[600px]"
            title={file.name || `PDF ${fileId}`}
            onLoad={() => {
              console.log(`PDF ${fileId} загружен в iframe`);
              if (isMounted.current) {
                setLoadingProgress(100);
                setLoading(false);
              }
            }}
            onError={(e) => {
              console.error(`Ошибка загрузки PDF ${fileId} в iframe:`, e);
            }}
          />
        </div>
      );
    } else {
      // Для изображений и по умолчанию
      return (
        <div className="relative min-h-[300px] flex items-center justify-center">
          <img
            src={imageUrl}
            alt={file.name || `File ${fileId}`}
            className="object-contain w-full h-full max-h-[600px]"
            onLoad={() => {
              console.log(`Изображение ${fileId} успешно загружено`);
              if (isMounted.current) {
                setLoadingProgress(100);
                setLoading(false);
              }
            }}
            onError={(e) => {
              console.error(`Ошибка отображения изображения ${fileId}:`, e);
              if (isMounted.current) {
                // Если прямая ссылка не работает, попробуем прокси
                if (imageUrl && imageUrl.startsWith('http') && imageUrl.includes('crm.steklolux.kz') && dealId) {
                  console.log(`🔄 Пробуем загрузить через прокси для файла ${fileId}`);
                  const token = localStorage.getItem('token');
                  const proxyUrl = `${API_URL}/api/deal/${dealId}/file/${fileId}?token=${token}`;
                  setImageUrl(proxyUrl);
                  return; // Не показываем ошибку, даем прокси шанс
                }
                
                const errorMsg = `Не удалось загрузить изображение (${fileType})`;
                setError(errorMsg);
                setLoading(false);
                if (onError) onError(errorMsg);
              }
            }}
          />
        </div>
      );
    }
  };

  // Показываем разные фоны в зависимости от статуса загрузки
  const getCardBackground = () => {
    if (isCurrentlyLoading) {
      // Когда этот файл активно загружается - выделяем его светло-голубым фоном
      return "bg-blue-50";
    } else if (loading) {
      // Во время загрузки более светлый фон
      return "bg-gray-50";
    } else if (error) {
      // При ошибке очень слабый красный фон
      return "bg-red-50";
    } else if (imageUrl) {
      // Успешно загружено - белый фон
      return "bg-white";
    }
    return "bg-gray-50"; // Файл ожидает загрузки
  };

  return (
    <div className={`relative border rounded-lg overflow-hidden ${getCardBackground()} transition-all duration-300`}>
      <div className="absolute top-2 right-2 z-20">
        {file.name && (
          <span className="bg-white/80 px-1 py-0.5 rounded text-xs shadow-sm">
            {file.name}
          </span>
        )}
      </div>
      
      {/* Индикатор статуса в углу */}
      <div className="absolute top-2 left-2 z-20">
        {isCurrentlyLoading && (
          <span className="flex items-center bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
            {/* Простой круговой спиннер */}
            <div className="animate-spin w-4 h-4 mr-1 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            Загрузка...
          </span>
        )}
        {!isCurrentlyLoading && loading && (
          <span className="flex items-center bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
            <svg className="w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            В очереди
          </span>
        )}
        {!loading && error && (
          <span className="flex items-center bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
            <svg className="w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Ошибка
          </span>
        )}
        {!loading && !error && imageUrl && (
          <span className="flex items-center bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
            
          </span>
        )}
        {!loading && !error && !imageUrl && !isCurrentlyLoading && (
          <span className="flex items-center bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
            <svg className="w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Ожидание
          </span>
        )}
      </div>

      {isCurrentlyLoading && renderLoadingIndicator()}

      {error && (
        <div className="p-4 bg-red-50 rounded-lg">
          <p className="text-red-500 text-sm mb-2">{error}</p>
          <p className="text-gray-700 text-xs mb-2">
            ID: {fileId} | Тип: {fileType}
            {dealId && ` | Сделка: ${dealId}`}
          </p>
          <button
            onClick={handleRetry}
            className="text-sm text-blue-500 hover:text-blue-700 px-3 py-1 bg-white rounded border border-blue-300"
          >
            Попробовать снова
          </button>
        </div>
      )}

      {renderContent()}

      {!loading && !error && imageUrl && (
        <div className="p-2 bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
          <div>
            ID: {fileId} | Тип: {fileType} 
            {dealId && ` | Сделка: ${dealId}`}
          </div>
          {fileCache.has(fileId) && (
            <span className="text-green-500">Кэшировано</span>
          )}
        </div>
      )}
    </div>
  );
};

// Компонент-контейнер для управления группой файлов
const FilesContainer = ({ files, dealId }) => {
  const [loadingStats, setLoadingStats] = useState({
    total: files.length,
    loaded: 0,
    inQueue: 0,
    currentFile: null
  });
  
  // Специальный эффект для централизованного управления загрузкой файлов
  useEffect(() => {
    console.log("Инициализация загрузки файлов", { filesCount: files.length, dealId });
    
    // Проверяем, все ли файлы уже загружены
    const allFilesLoaded = files.every(file => fileCache.has(file.id));
    
    if (allFilesLoaded) {
      console.log("Все файлы уже загружены, пропускаем инициализацию очереди");
      setLoadingStats({
        total: files.length,
        loaded: files.length,
        inQueue: 0,
        currentFile: null
      });
      return;
    }
    
    // Не сбрасываем историю, чтобы не перезагружать уже успешно загруженные файлы
    const filesToLoad = files.filter(file => !fileCache.has(file.id));
    console.log(`Найдено ${filesToLoad.length} файлов для загрузки (без сброса истории)`);
    
    return () => {
      // При размонтировании компонента НЕ останавливаем обработку
      // Позволяем файлам загрузиться до конца
      console.log("FilesContainer размонтирован");
    };
  }, [files.length, dealId]); // Зависим только от количества файлов и dealId, а не от самого массива files
  
  // Отслеживаем текущий загружаемый файл и статистику
  useEffect(() => {
    const updateStats = () => {
      const cachedCount = files.filter(file => fileCache.has(file.id)).length;
      const remainingCount = files.length - cachedCount;
      
      setLoadingStats({
        total: files.length,
        loaded: cachedCount,
        inQueue: remainingCount,
        currentFile: fileQueue.currentFileId
      });
      
      // Если все файлы загружены и очередь пуста, но процесс всё еще активен - остановить его
      if (cachedCount === files.length && fileQueue.queue.length === 0 && fileQueue.processing) {
        console.log("Все файлы загружены, принудительно останавливаем процесс");
        fileQueue.processing = false;
        fileQueue.currentFileId = null;
        fileQueue.notifyListeners(null);
      }
    };
    
    // Подписываемся на обновления очереди
    const unsubscribe = fileQueue.subscribe(() => {
      updateStats();
    });
    
    // Начальное обновление
    updateStats();
    
    return () => {
      unsubscribe();
    };
  }, [files]);
  
  if (!files || !Array.isArray(files) || files.length === 0) {
    return <div className="p-4 text-gray-500 border rounded-lg">Нет файлов для отображения</div>;
  }
  
  // Находим текущий загружаемый файл и его индекс
  const currentFileIndex = files.findIndex(file => file.id === loadingStats.currentFile);
  const currentFileName = currentFileIndex >= 0 ? (files[currentFileIndex].name || `Файл ${currentFileIndex + 1}`) : 'Нет';
  
  // Рассчитываем процент загруженных файлов
  const loadedPercent = Math.round((loadingStats.loaded / loadingStats.total) * 100) || 0;
  
  return (
    <div className="space-y-6">
      
      
      <div className="space-y-6">
        {files.map((file, index) => (
          <FilePreview 
            key={file.id} 
            file={file} 
            dealId={dealId}
            index={index}
            totalFiles={files.length}
            onError={(error) => console.error(`Ошибка файла ${file.id}:`, error)}
          />
        ))}
      </div>
    </div>
  );
};

export { FilePreview, FilesContainer };
export default FilePreview;